import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Loader2, UserPlus } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';

/**
 * Assign a CW member to a ticket as a Resource (no time block, no calendar
 * reservation). Mirrors CW's "Add Resource" UX:
 *   1. Pick the member
 *   2. Optional: tick "Notify resource by email" + supply a message that
 *      becomes an internal note CW sends with the notification
 *
 * Use this when the admin wants to assign someone but doesn't have a specific
 * time/date — for that, the "+ Schedule" button creates a calendar-blocked
 * entry instead.
 */
export default function AdminAddResourceModal({ ticketId, onClose, onSuccess }) {
  const [memberId, setMemberId] = useState('');
  const [notify, setNotify] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const { data: membersData } = useQuery({
    queryKey: ['admin-lookup-members'],
    queryFn: () => adminLookupAPI.members(),
  });
  const members = membersData?.data?.items || [];

  const addMutation = useMutation({
    mutationFn: (data) => adminTicketsAPI.addResource(ticketId, data),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to add resource'),
  });

  const canSubmit = !!memberId && !addMutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Add Resource
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Member *
            </label>
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            >
              <option value="">Pick a CW member…</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Assigns this member to the ticket. No time is reserved on their calendar — use
              "+ Schedule" instead if you want to block a specific time.
            </div>
          </div>

          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
            <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              Notify resource by email
            </label>
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Triggers ConnectWise's notify-on-add for this ticket.
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Message (optional)
            </label>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add an internal note that goes out with the notification…"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              If supplied, this is added as an internal note (visible to techs in CW, hidden
              from the customer portal).
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => addMutation.mutate({ memberId: parseInt(memberId), notify, message })}
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {addMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Add Resource
          </button>
        </div>
      </div>
    </div>
  );
}
